import React, { useEffect, useState, useRef } from "react";
import {
  BsPersonFill,
  BsCalendar3,
  BsCheckCircle,
  BsDashCircle,
  BsSearch,
  BsEye,
  BsEyeSlash,
} from "react-icons/bs";
import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config";
import NoDataFound from "../../../img/no.png";
import { sweetAlert } from "../../../utils/UniversalFunction";

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Individual training row                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */
function TrainingRow({ index, trainingItem, trainingType, onStatusChange, categories }) {
  const [isOpen, setIsOpen] = useState(false);

  const trainerNames = Array.isArray(trainingItem.trainers)
    ? trainingItem.trainers.map((t) => t.name).join(", ")
    : trainingItem.trainers || "";

  const displayDate = trainingItem.fromDate
    ? new Date(trainingItem.fromDate).toLocaleDateString("en-CA")
    : "";

  const isAttended = trainingType === "COMPLETED";

  const categoryTags = Array.isArray(trainingItem.categoryIds)
    ? trainingItem.categoryIds
        .map((id) => categories.find((c) => c.id === id)?.title)
        .filter(Boolean)
    : [];

  const dateRange = (() => {
    const from = trainingItem.fromDate
      ? new Date(trainingItem.fromDate).toLocaleDateString("en-CA")
      : "";
    const to = trainingItem.toDate
      ? new Date(trainingItem.toDate).toLocaleDateString("en-CA")
      : "";
    if (from && to) return `${from} – ${to}`;
    return from || to || "—";
  })();

  return (
    <>
    <div style={rowStyles.row}>
      {/* ── Index badge ── */}
      <div style={rowStyles.badge}>
        <span style={rowStyles.badgeNum}>{index + 1}</span>
      </div>

      {/* ── Content ── */}
      <div style={rowStyles.content}>
        <span style={rowStyles.title}>{trainingItem.trainingTitle}</span>

        <div style={rowStyles.metaRow}>
          {/* Status */}
          {isAttended ? (
            <span style={rowStyles.statusAttended}>
              <BsPersonFill style={{ width: 11, height: 11, marginRight: 3, flexShrink: 0 }} />
              Attended
            </span>
          ) : (
            <span style={rowStyles.statusNotAttended}>
              <BsPersonFill style={{ width: 11, height: 11, marginRight: 3, flexShrink: 0 }} />
              Not attended
            </span>
          )}

          {/* Trainer */}
          {trainerNames && (
            <span style={rowStyles.metaItem}>
              <BsPersonFill style={{ width: 11, height: 11, color: "#888", marginRight: 3, flexShrink: 0 }} />
              {trainerNames}
            </span>
          )}

          {/* Date */}
          {displayDate && (
            <span style={rowStyles.metaItem}>
              <BsCalendar3 style={{ width: 10, height: 10, color: "#888", marginRight: 3, flexShrink: 0 }} />
              {displayDate}
            </span>
          )}

          {/* Category tags */}
          {categoryTags.map((cat, i) => (
            <span key={i} style={rowStyles.categoryTag}>
              {cat}
            </span>
          ))}
        </div>
      </div>

      {/* ── Action button ── */}
      <div style={rowStyles.actions}>
        {isAttended ? (
          <button
            style={{ ...rowStyles.actionBtn, ...rowStyles.actionBtnRed }}
            onClick={() => onStatusChange(trainingItem.id, "NON_ATTENDANT")}
          >
            <BsDashCircle style={{ width: 13, height: 13, marginRight: 5, flexShrink: 0 }} />
            Mark not attended
          </button>
        ) : (
          <button
            style={{ ...rowStyles.actionBtn, ...rowStyles.actionBtnGreen }}
            onClick={() => onStatusChange(trainingItem.id, "ATTENDANT")}
          >
            <BsCheckCircle style={{ width: 13, height: 13, marginRight: 5, flexShrink: 0 }} />
            Mark attended
          </button>
        )}

        <button
          style={rowStyles.eyeBtn}
          onClick={() => setIsOpen((p) => !p)}
          title="View details"
        >
          {isOpen
            ? <BsEye style={{ width: 15, height: 15 }} />
            : <BsEyeSlash style={{ width: 15, height: 15 }} />}
        </button>
      </div>
    </div>

    {/* ── Detail panel ── */}
    {isOpen && (
      <div style={rowStyles.detailPanel}>
        <div style={rowStyles.detailGrid}>
          <div style={rowStyles.detailItem}>
            <span style={rowStyles.detailLabel}>Trainer</span>
            <span style={rowStyles.detailValue}>{trainerNames || "—"}</span>
          </div>
          <div style={rowStyles.detailItem}>
            <span style={rowStyles.detailLabel}>Date</span>
            <span style={rowStyles.detailValue}>{dateRange}</span>
          </div>
          <div style={rowStyles.detailItem}>
            <span style={rowStyles.detailLabel}>Time</span>
            <span style={rowStyles.detailValue}>
              {trainingItem.fromTime && trainingItem.toTime
                ? `${trainingItem.fromTime} – ${trainingItem.toTime}`
                : trainingItem.fromTime || "—"}
            </span>
          </div>
          <div style={rowStyles.detailItem}>
            <span style={rowStyles.detailLabel}>Mode</span>
            <span style={rowStyles.detailValue}>
              {trainingItem.modeOfTraining === "OFFLINE" ? "Offline" : "Online"}
            </span>
          </div>
          <div style={rowStyles.detailItem}>
            <span style={rowStyles.detailLabel}>Categories</span>
            <span style={rowStyles.detailValue}>
              {categoryTags.length > 0 ? (
                <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {categoryTags.map((cat, i) => (
                    <span key={i} style={rowStyles.categoryTag}>{cat}</span>
                  ))}
                </span>
              ) : "—"}
            </span>
          </div>
          {trainingItem.linkOrVenues && (
            <div style={rowStyles.detailItem}>
              <span style={rowStyles.detailLabel}>
                {trainingItem.modeOfTraining === "OFFLINE" ? "Venue" : "Link"}
              </span>
              <span style={rowStyles.detailValue}>
                {trainingItem.linkOrVenues.startsWith("http") ? (
                  <a href={trainingItem.linkOrVenues} target="_blank" rel="noopener noreferrer"
                     style={{ color: "#3F88A5" }}>
                    {trainingItem.linkOrVenues}
                  </a>
                ) : (
                  trainingItem.linkOrVenues
                )}
              </span>
            </div>
          )}
        </div>
        {trainingItem.description && (
          <>
            <hr style={{ margin: "12px 0", borderColor: "#e5e7eb" }} />
            <div>
              <span style={{ fontWeight: 600, color: "#3F88A5", fontSize: 13 }}>Description</span>
              <p style={{ marginTop: 6, color: "#444", fontSize: 13, marginBottom: 0 }}>
                {trainingItem.description}
              </p>
            </div>
          </>
        )}
      </div>
    )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Main TrainingList                                                           */
/* ─────────────────────────────────────────────────────────────────────────── */
const TrainingList = ({ updateCheck, setUpdateCheck, financialYearId, setFinancialYearId }) => {
  const isMounted = useRef(true);

  const [upcomingList, setUpcomingList] = useState([]);
  const [completedList, setCompletedList] = useState([]);
  const [notCompletedList, setNotCompletedList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  const [activeFilter, setActiveFilter] = useState("ALL");
  const [activeCategoryId, setActiveCategoryId] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getTrainingCategory`,
        {},
        {},
        "GET"
      );
      if (isSuccess && isMounted.current) setCategories(data.data || []);
    };

    const fetchFinancialYears = async () => {
      const stored = localStorage.getItem("financialYearData");
      if (stored) {
        const reversed = [...JSON.parse(stored)].reverse();
        setFinancialYears(reversed);
        if (reversed.length && !financialYearId) setFinancialYearId(reversed[0].id);
      } else {
        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getFinancialYear`,
          {},
          {}
        );
        if (isSuccess && isMounted.current) {
          localStorage.setItem("financialYearData", JSON.stringify(data.data));
          const reversed = [...data.data].reverse();
          setFinancialYears(reversed);
          if (reversed.length) setFinancialYearId(reversed[0].id);
        }
      }
    };

    fetchCategories();
    fetchFinancialYears();
  }, []);

  const fetchList = async (type) => {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getTraineeData`,
      {},
      { type, financialYearId },
      "GET"
    );
    return isSuccess && isMounted.current ? data.data || [] : [];
  };

  const fetchAll = async () => {
    if (!financialYearId) return;
    const [upcoming, completed, notCompleted] = await Promise.all([
      fetchList("UPCOMING"),
      fetchList("COMPLETED"),
      fetchList("NOT_COMPLETED"),
    ]);
    if (isMounted.current) {
      setUpcomingList(upcoming);
      setCompletedList(completed);
      setNotCompletedList(notCompleted);
    }
  };

  useEffect(() => { fetchAll(); }, [financialYearId, updateCheck]);

  const validatedTrainingStatus = async (trainingId, trainingStatus) => {
    const { isSuccess } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}validatedTrainingStatus`,
      {},
      { trainingId, trainingStatus },
      "POST",
      true,
      false
    );
    if (isSuccess && isMounted.current) {
      sweetAlert("success", trainingStatus === "ATTENDANT" ? "Marked Attended" : "Marked Not Attended");
      setUpdateCheck(Math.random());
    }
  };

  const allTrainings = [
    ...upcomingList.map((i) => ({ ...i, _type: "UPCOMING" })),
    ...completedList.map((i) => ({ ...i, _type: "COMPLETED" })),
    ...notCompletedList.map((i) => ({ ...i, _type: "NOT_COMPLETED" })),
  ];

  const filtered = allTrainings.filter((item) => {
    const matchesStatus =
      activeFilter === "ALL" || item._type === activeFilter;

    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      (item.trainingTitle || "").toLowerCase().includes(q) ||
      (Array.isArray(item.trainers)
        ? item.trainers.map((t) => t.name).join(", ")
        : item.trainers || ""
      ).toLowerCase().includes(q);

    const matchesCategory =
      activeCategoryId === "ALL" ||
      (Array.isArray(item.categoryIds) &&
        item.categoryIds.includes(Number(activeCategoryId)));

    const itemDate = item.fromDate ? new Date(item.fromDate) : null;
    const matchesDateFrom = !dateFrom || (itemDate && itemDate >= new Date(dateFrom));
    const matchesDateTo = !dateTo || (itemDate && itemDate <= new Date(dateTo + "T23:59:59"));

    return matchesStatus && matchesSearch && matchesCategory && matchesDateFrom && matchesDateTo;
  });

  const attendedCount = completedList.length;
  const notAttendedCount = notCompletedList.length + upcomingList.length;

  /* ────────────────────────────── RENDER ─────────────────────────────────── */
  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>

        {/* ── Row 1: year selector + search ── */}
        <div style={styles.toolbarRow}>
          <select
            value={financialYearId || ""}
            onChange={(e) => setFinancialYearId(e.target.value)}
            style={styles.yearSelect}
          >
            <option value="">Select Year</option>
            {financialYears.map((item) => (
              <option key={item.id} value={item.id}>
                {item.financial_year_value}
              </option>
            ))}
          </select>

          <div style={styles.searchWrapper}>
            <BsSearch style={{ ...styles.searchIcon, width: 14, height: 14 }} />
            <input
              type="text"
              placeholder="Search by name or trainer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>
        </div>

        {/* ── Row 2: filters ── */}
        <div style={styles.filtersRow}>
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            style={{
              ...styles.filterSelect,
              borderColor: activeFilter !== "ALL" ? "#3F88A5" : "#ccc",
              color: activeFilter !== "ALL" ? "#3F88A5" : "#333",
            }}
          >
            <option value="ALL">All statuses</option>
            <option value="COMPLETED">Attended</option>
            <option value="NOT_COMPLETED">Not attended</option>
            <option value="UPCOMING">Upcoming</option>
          </select>

          <select
            value={activeCategoryId}
            onChange={(e) => setActiveCategoryId(e.target.value)}
            style={{
              ...styles.filterSelect,
              borderColor: activeCategoryId !== "ALL" ? "#3F88A5" : "#ccc",
              color: activeCategoryId !== "ALL" ? "#3F88A5" : "#333",
            }}
          >
            <option value="ALL">All categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={String(cat.id)}>
                {cat.title}
              </option>
            ))}
          </select>

          <div style={styles.dateRangeWrapper}>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ ...styles.datePicker, color: dateFrom ? "#333" : "#999", cursor: "pointer" }}
            />
            <span style={styles.dateSep}>-</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ ...styles.datePicker, color: dateTo ? "#333" : "#999", cursor: "pointer" }}
            />
          </div>

          {(activeFilter !== "ALL" || activeCategoryId !== "ALL" || dateFrom || dateTo || searchQuery) && (
            <button
              style={styles.clearBtn}
              onClick={() => {
                setActiveFilter("ALL");
                setActiveCategoryId("ALL");
                setDateFrom("");
                setDateTo("");
                setSearchQuery("");
              }}
            >
              ✕ Clear filters
            </button>
          )}
        </div>

        {/* ── Summary bar ── */}
        <div style={styles.summaryBar}>
          <span style={styles.summaryText}>
            Showing {filtered.length} of {allTrainings.length} trainings
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <span style={{ ...styles.countBadge, color: "#1B8A3D", backgroundColor: "#E6F5ED" }}>
              ✓ {attendedCount} attended
            </span>
            <span style={{ ...styles.countBadge, color: "#B80000", backgroundColor: "#FDECEA" }}>
              ✕ {notAttendedCount} not attended
            </span>
          </div>
        </div>

        {/* ── List ── */}
        {filtered.length > 0 ? (
          <div style={{ marginTop: 8 }}>
            {filtered.map((item, index) => (
              <TrainingRow
                key={`${item._type}-${item.id}-${index}`}
                index={index}
                trainingItem={item}
                trainingType={item._type}
                onStatusChange={validatedTrainingStatus}
                categories={categories}
              />
            ))}
          </div>
        ) : (
          <div className="hstack justify-content-center" style={{ padding: "40px 0" }}>
            <img src={NoDataFound} alt="No data" />
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Styles                                                                     */
/* ─────────────────────────────────────────────────────────────────────────── */
const styles = {
  wrapper: { padding: "0 20px 20px" },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: "20px 20px 16px",
    fontFamily: "Arial, sans-serif",
  },
  toolbarRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginBottom: 10,
  },
  yearSelect: {
    padding: "8px 14px",
    border: "1.5px solid #d1d5db",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    color: "#333",
    backgroundColor: "#fff",
    cursor: "pointer",
    outline: "none",
    flexShrink: 0,
    minWidth: 130,
  },
  searchWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    flex: 1,
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#aaa",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    padding: "9px 12px 9px 34px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 13,
    color: "#333",
    outline: "none",
    boxSizing: "border-box",
  },
  filtersRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginBottom: 12,
    flexWrap: "wrap",
  },
  filterSelect: {
    padding: "7px 12px",
    border: "1.5px solid #ccc",
    borderRadius: 8,
    fontSize: 13,
    color: "#333",
    backgroundColor: "#fff",
    cursor: "pointer",
    minWidth: 130,
  },
  dateRangeWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "4px 10px",
    backgroundColor: "#fff",
  },
  datePicker: {
    border: "none",
    outline: "none",
    fontSize: 13,
    color: "#999",
    backgroundColor: "transparent",
    cursor: "default",
  },
  dateSep: {
    color: "#aaa",
    fontSize: 13,
  },
  clearBtn: {
    padding: "6px 12px",
    border: "1.5px solid #ccc",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "#666",
    backgroundColor: "transparent",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  summaryBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    flexWrap: "wrap",
    gap: 6,
  },
  summaryText: {
    fontSize: 12,
    color: "#666",
  },
  countBadge: {
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 20,
    padding: "3px 10px",
    display: "flex",
    alignItems: "center",
  },
};

const rowStyles = {
  row: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: 8,
    gap: 14,
  },
  badge: {
    backgroundColor: "#3a7a96",
    width: 34,
    height: 34,
    borderRadius: 8,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  badgeNum: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "bold",
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },
  title: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  statusAttended: {
    fontSize: 12,
    fontWeight: 600,
    color: "#1B8A3D",
    display: "flex",
    alignItems: "center",
  },
  statusNotAttended: {
    fontSize: 12,
    fontWeight: 600,
    color: "#B80000",
    display: "flex",
    alignItems: "center",
  },
  metaItem: {
    fontSize: 12,
    color: "#555",
    display: "flex",
    alignItems: "center",
  },
  categoryTag: {
    fontSize: 11,
    color: "#444",
    border: "1px solid #ccc",
    borderRadius: 20,
    padding: "1px 9px",
    backgroundColor: "#fff",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  actionBtn: {
    border: "1.5px solid",
    borderRadius: 8,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: "transparent",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
  },
  actionBtnGreen: {
    borderColor: "#1B8A3D",
    color: "#1B8A3D",
    backgroundColor: "#E6F5ED",
  },
  actionBtnRed: {
    borderColor: "#B80000",
    color: "#B80000",
    backgroundColor: "#FDECEA",
  },
  eyeBtn: {
    border: "1.5px solid #ccc",
    borderRadius: 6,
    padding: "5px 7px",
    backgroundColor: "transparent",
    cursor: "pointer",
    color: "#666",
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  detailPanel: {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "14px 16px",
    marginBottom: 8,
    backgroundColor: "#fafafa",
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: "10px 20px",
  },
  detailItem: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  detailValue: {
    fontSize: 13,
    color: "#333",
  },
};

export default TrainingList;
